import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { trackerApi } from './tracker-api';
import { Offer, Campaign, AffiliateNetwork } from './api-interfaces';
import { Platform, PlatformFilter } from './platformSlice';

interface UnifiedOffer extends Offer {
  platform: Platform;
  migrationStatus?: 'pending' | 'in_progress' | 'completed' | 'failed';
  migrationDate?: string;
  voluumId?: string;
  redtrackId?: string;
}

interface UnifiedCampaign extends Campaign {
  platform: Platform;
  migrationStatus?: 'pending' | 'in_progress' | 'completed' | 'failed';
  migrationDate?: string;
  voluumId?: string;
  redtrackId?: string;
}

interface UnifiedAffiliateNetwork extends AffiliateNetwork {
  platform: Platform;
  migrationStatus?: 'pending' | 'in_progress' | 'completed' | 'failed';
  migrationDate?: string;
  voluumId?: string;
  redtrackId?: string;
}

interface TrackerState {
  entities: {
    offer: {
      voluum: Offer[];
      redtrack: Offer[];
      unified: UnifiedOffer[];
    };
    campaign: {
      voluum: Campaign[];
      redtrack: Campaign[];
      unified: UnifiedCampaign[];
    };
    'affiliate-network': {
      voluum: AffiliateNetwork[];
      redtrack: AffiliateNetwork[];
      unified: UnifiedAffiliateNetwork[];
    };
  };
  platformFilter: PlatformFilter;
  loading: {
    voluum: boolean;
    redtrack: boolean;
  };
  error: {
    voluum: string | null;
    redtrack: string | null;
  };
}

export type TrackerEntity = keyof TrackerState['entities'];

const initialState: TrackerState = {
  entities: {
    offer: {
      voluum: [],
      redtrack: [],
      unified: [],
    },
    campaign: {
      voluum: [],
      redtrack: [],
      unified: [],
    },
    'affiliate-network': {
      voluum: [],
      redtrack: [],
      unified: [],
    },
  },
  platformFilter: 'both',
  loading: {
    voluum: false,
    redtrack: false,
  },
  error: {
    voluum: null,
    redtrack: null,
  },
};

const trackerData = createSlice({
  name: 'trackerData',
  initialState,
  reducers: {
    setPlatformFilter: (state, action: PayloadAction<PlatformFilter>) => {
      state.platformFilter = action.payload;
    },
    setPlatformLoading: (state, action: PayloadAction<{ platform: Platform; loading: boolean }>) => {
      state.loading[action.payload.platform] = action.payload.loading;
    },
    setPlatformError: (state, action: PayloadAction<{ platform: Platform; error: string | null }>) => {
      state.error[action.payload.platform] = action.payload.error;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        trackerApi.endpoints.findTrackerEntities.matchPending,
        (state, { meta }) => {
          const platform = meta.arg.originalArgs.platform || 'both';
          if (platform === 'both') {
            state.loading.voluum = true;
            state.loading.redtrack = true;
          } else if (platform === 'voluum' || platform === 'redtrack') {
            state.loading[platform] = true;
          }
          state.error.voluum = null;
          state.error.redtrack = null;
        }
      )
      .addMatcher(
        trackerApi.endpoints.findTrackerEntities.matchFulfilled,
        (state, { payload, meta }) => {
          const endpoint: TrackerEntity = meta.arg.originalArgs.endpoint;
          const platform = meta.arg.originalArgs.platform || 'both';
          
          // Reset loading states
          if (platform === 'both') {
            state.loading.voluum = false;
            state.loading.redtrack = false;
          } else if (platform === 'voluum' || platform === 'redtrack') {
            state.loading[platform] = false;
          }
          
          // Handle platform-specific data storage
          switch (endpoint) {
            case 'offer':
              if (payload.voluum) state.entities.offer.voluum = payload.voluum;
              if (payload.redtrack) state.entities.offer.redtrack = payload.redtrack;
              if (payload.unified) state.entities.offer.unified = payload.unified;
              // Legacy support - if old format, store in unified
              if (payload.offers && !payload.voluum && !payload.redtrack) {
                state.entities.offer.unified = payload.offers;
              }
              break;
            case 'campaign':
              if (payload.voluum) state.entities.campaign.voluum = payload.voluum;
              if (payload.redtrack) state.entities.campaign.redtrack = payload.redtrack;
              if (payload.unified) state.entities.campaign.unified = payload.unified;
              // Legacy support
              if (payload.campaigns && !payload.voluum && !payload.redtrack) {
                state.entities.campaign.unified = payload.campaigns;
              }
              break;
            case 'affiliate-network':
              if (payload.voluum) state.entities['affiliate-network'].voluum = payload.voluum;
              if (payload.redtrack) state.entities['affiliate-network'].redtrack = payload.redtrack;
              if (payload.unified) state.entities['affiliate-network'].unified = payload.unified;
              // Legacy support
              if (payload.affiliateNetworks && !payload.voluum && !payload.redtrack) {
                state.entities['affiliate-network'].unified = payload.affiliateNetworks;
              }
              break;
          }
        }
      )
      .addMatcher(
        trackerApi.endpoints.findTrackerEntities.matchRejected,
        (state, action) => {
          const platform = action.meta.arg.originalArgs.platform || 'both';
          if (platform === 'both') {
            state.loading.voluum = false;
            state.loading.redtrack = false;
            state.error.voluum = action.error.message || null;
            state.error.redtrack = action.error.message || null;
          } else if (platform === 'voluum' || platform === 'redtrack') {
            state.loading[platform] = false;
            state.error[platform] = action.error.message || null;
          }
        }
      )
      .addMatcher(
        trackerApi.endpoints.getTrackerEntity.matchFulfilled,
        (state, action) => {
          const { endpoint, platform } = action.meta.arg.originalArgs;
          const id = (action.meta.arg.originalArgs as any).id;
          
          if (platform && platform !== 'both') {
            // Use type assertion to handle the dynamic indexing
            const entityGroup = state.entities[endpoint] as any;
            if (entityGroup && entityGroup[platform]) {
              const entityArray = entityGroup[platform];
              const index = entityArray.findIndex((entity: any) => entity.id === id);
              if (index !== -1) {
                entityArray[index] = action.payload;
              } else {
                entityArray.push(action.payload);
              }
            }
          } else {
            // Default to unified if no platform specified
            const entityArray = state.entities[endpoint].unified;
            const index = entityArray.findIndex((entity: any) => entity.id === id);
            if (index !== -1) {
              entityArray[index] = action.payload;
            } else {
              entityArray.push(action.payload);
            }
          }
        }
      )
      .addMatcher(
        trackerApi.endpoints.deleteTrackerEntity.matchFulfilled,
        (state, action) => {
          const { id, platform } = action.meta.arg.originalArgs;
          const endpoint: TrackerEntity = action.meta.arg.originalArgs.endpoint;
          
          if (platform && platform !== 'both') {
            // Use type assertion to handle the dynamic indexing
            const entityGroup = state.entities[endpoint] as any;
            if (entityGroup && entityGroup[platform]) {
              entityGroup[platform] = entityGroup[platform].filter((entity: any) => entity.id !== id);
            }
          } else {
            // Remove from all platforms if no specific platform
            const entityGroup = state.entities[endpoint] as any;
            if (entityGroup) {
              entityGroup.voluum = entityGroup.voluum.filter((entity: any) => entity.id !== id);
              entityGroup.redtrack = entityGroup.redtrack.filter((entity: any) => entity.id !== id);
              entityGroup.unified = entityGroup.unified.filter((entity: any) => entity.id !== id);
            }
          }
        }
      );
  },
});

export const { setPlatformFilter, setPlatformLoading, setPlatformError } = trackerData.actions;

export default trackerData.reducer;

// Export the unified interfaces for use in components
export type { UnifiedOffer, UnifiedCampaign, UnifiedAffiliateNetwork };